/**
 * Triple Box Bundle Component
 * Allows customers to select 3 different box configurations and add them all to cart
 */
(function () {
  'use strict';

  if (!customElements.get('triple-box-bundle')) {
    class TripleBoxBundle extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.uniqueImagesOnly = this.dataset.uniqueImagesOnly === 'true';
        this.sortOption = this.dataset.sortOption || 'alphabetical';
        this.targetPrice = parseFloat(this.dataset.targetPrice) || 100;

        // State for 3 boxes
        this.boxes = {
          1: { startingProduct: null, selectedProduct: null },
          2: { startingProduct: null, selectedProduct: null },
          3: { startingProduct: null, selectedProduct: null }
        };

        this.activeBox = 1;
        this.parsedProducts = [];
        this.currentBrowsing = null;
        this.isProcessing = false;
        this.isAddingToCart = false;
      }

      connectedCallback() {
        if (!this.isConnected) return;

        // Restore transferred state if available
        if (this._transferredState) {
          this.boxes = this._transferredState.boxes;
          this.activeBox = this._transferredState.activeBox;
          this.parsedProducts = this._transferredState.parsedProducts;
          this.targetPrice = this._transferredState.targetPrice || this.targetPrice;
          this._transferredState = null;

          // After init, restore the preview images for selected boxes
          setTimeout(() => this.restoreSelectedBoxPreviews(), 100);
        }

        this.init();
      }

      disconnectedCallback() {
        // Cleanup handled by garbage collection
      }

      init() {
        this.initializeBoxPreviews();
        this.setupStepListeners();
        this.setupButtonListener();

        // Only load swatches if we don't already have them
        if (this.parsedProducts.length === 0) {
          this.loadSwatchesForActiveBox().then(() => {
            this.initializeDefaultSelection();
          });
        } else {
          // Re-render existing swatches
          const starting = this.boxes[this.activeBox].startingProduct;
          if (starting) {
            this.renderSwatches(starting.flowerColor, starting.boxColor);
          }
          setTimeout(() => this.restoreSelectedBoxPreviews(), 50);
        }

        this.updateUI();

        if (this.parsedProducts.length > 0) {
          setTimeout(() => this.restoreSelectedBoxPreviews(), 150);
        }
      }

      restoreSelectedBoxPreviews() {
        for (let i = 1; i <= 3; i++) {
          const box = this.boxes[i];
          if (box.selectedProduct) {
            const preview = this.querySelector(`[data-box-number="${i}"]`);
            if (!preview) continue;

            const imgEl = preview.querySelector('[data-box-image]');
            if (imgEl && box.selectedProduct.image) {
              imgEl.src = box.selectedProduct.image;
            }

            const titleEl = preview.querySelector('[data-box-title]');
            if (titleEl) {
              titleEl.textContent = box.selectedProduct.title;
            }

            // Restore target price (bundle price)
            const priceEl = preview.querySelector('[data-box-price] .first-step--price');
            if (priceEl && box.selectedProduct.displayPrice) {
              priceEl.textContent = box.selectedProduct.displayPrice;
            }

            // Restore compare-at price (actual product price) - keep it visible
            const priceContainer = preview.querySelector('[data-box-price]');
            let comparePrice = preview.querySelector('[data-box-price] .first-step--compare_at_price');
            if (box.selectedProduct.comparePrice) {
              if (!comparePrice && priceContainer) {
                comparePrice = document.createElement('span');
                comparePrice.className = 'first-step--compare_at_price';
                priceContainer.appendChild(comparePrice);
              }
              if (comparePrice) {
                comparePrice.textContent = box.selectedProduct.comparePrice;
                comparePrice.style.display = '';
              }
            }

            const subtitleEl = preview.querySelector('[data-box-subtitle]');
            if (subtitleEl) {
              subtitleEl.textContent = 'Selected';
            }

            const badge = preview.querySelector('[data-box-badge]');
            if (badge) {
              badge.setAttribute('data-status', 'selected');
              badge.textContent = 'SELECTED';
            }

            preview.setAttribute('data-status', 'selected');
          }
        }
      }

      initializeBoxPreviews() {
        const previews = this.querySelectorAll('.first-step--box-preview');
        previews.forEach((preview, index) => {
          const boxNum = index + 1;
          const startingProduct = {
            id: preview.dataset.startingProductId,
            handle: preview.dataset.startingProductHandle,
            tag: preview.dataset.tag,
            boxColor: preview.dataset.boxColor,
            flowerColor: preview.dataset.flowerColor,
            url: preview.dataset.productUrl,
            image: preview.querySelector('img')?.src,
            title: preview.querySelector('[data-box-title]')?.textContent,
            price: preview.querySelector('[data-box-price] .first-step--price')?.textContent
          };

          this.boxes[boxNum].startingProduct = startingProduct;
          preview.addEventListener('click', () => this.switchToBox(boxNum));
        });
      }

      switchToBox(boxNum) {
        // Can't switch to a box that's already selected (no going back)
        if (this.boxes[boxNum].selectedProduct) {
          return;
        }

        // Can't switch to box 2 if box 1 not selected, etc.
        if (boxNum > 1 && !this.boxes[boxNum - 1].selectedProduct) {
          return;
        }

        // If clicking on already active box, do nothing
        if (this.activeBox === boxNum) {
          return;
        }

        this.activeBox = boxNum;

        this.loadSwatchesForActiveBox();
        this.updateUI();

        // Load the product for this box
        this.loadBoxStartingProduct(boxNum);
      }

      setupStepListeners() {
        // Listen for flower color changes (step 2)
        this.addEventListener('change', async (e) => {
          if (e.target.matches('input[name="second_step_product"]')) {
            const productIndex = parseInt(e.target.value);
            const selectedProduct = this.parsedProducts[productIndex];
            await this.updateForFlowerSelection(selectedProduct);
          }
        });

        // Listen for box color changes (step 3)
        this.addEventListener('change', async (e) => {
          if (e.target.matches('input[name="third_step_product"]')) {
            const flowerInput = this.querySelector('input[name="second_step_product"]:checked');
            if (!flowerInput) return;

            const productIndex = parseInt(flowerInput.value);
            const selectedProduct = this.parsedProducts[productIndex];
            const boxIndex = parseInt(e.target.value);
            const finalProduct = selectedProduct.boxes[boxIndex];

            await this.updateForBoxSelection(finalProduct);
          }
        });

        // Listen for gift card changes (step 4)
        this.addEventListener('change', (e) => {
          if (e.target.matches('input[name="properties[Gift Card Type]"]')) {
            this.handleGiftCardChange(e.target);
          }
        });
      }

      setupButtonListener() {
        const form = this.querySelector('form[data-type="triple-box-bundle-form"]');
        const button = this.querySelector('button[type="submit"]');

        if (!form || !button) return;

        // Intercept form submission BEFORE product-form handles it
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const selectedCount = Object.values(this.boxes).filter(b => b.selectedProduct).length;

          if (selectedCount === 2) {
            // Box 3 - auto-commit and add all to cart (skip UI update since we're resetting after)
            await this.confirmCurrentBox({ skipUIUpdate: true });
            await this.submitToCartAjax();
          } else if (selectedCount === 3) {
            // All boxes selected - add to cart via AJAX
            await this.submitToCartAjax();
          } else {
            // Box 1 or 2 - confirm current box
            await this.confirmCurrentBox();
          }
        }, true);
      }

      async submitToCartAjax() {
        this.isAddingToCart = true;

        const button = this.querySelector('button[type="submit"]');
        if (button) {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        }

        // Start loading bar
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

        try {
          const items = [];

          for (let i = 1; i <= 3; i++) {
            const box = this.boxes[i];
            if (!box.selectedProduct) continue;

            let variantId = box.selectedProduct.variantId;
            if (variantId && variantId.includes('gid://')) {
              const match = variantId.match(/\/(\d+)$/);
              if (match) variantId = match[1];
            }

            const item = {
              id: variantId,
              quantity: 1,
              properties: {}
            };

            // Add box-specific properties (includes gift card info saved at confirm time)
            Object.assign(item.properties, box.selectedProduct.properties);

            // Add bundle identifier
            item.properties['_box_number'] = i.toString();
            item.properties['_bundle'] = 'Triple Box Bundle';

            items.push(item);
          }

          // Prepare bundled sections for cart drawer
          let sectionsToBundle = [];
          document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          }));

          const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items,
              sections: sectionsToBundle.join(','),
              sections_url: window.location.pathname
            })
          });

          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }

          const responseJson = await response.json();

          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

          if (!response.ok) {
            throw new Error(responseJson.description || 'Failed to add to cart');
          }

          if (this.isAddingToCart) {
            const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
            cartContent['sections'] = responseJson['sections'];

            const form = this.querySelector('form');

            if (form) {
              form.dispatchEvent(new CustomEvent('variant:add', {
                bubbles: true,
                detail: {
                  items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
                  cart: cartContent,
                  onSuccessDo: 'force_open_drawer'
                }
              }));
            }

            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
              bubbles: true,
              detail: {
                baseEvent: 'variant:add',
                onSuccessDo: 'force_open_drawer',
                cart: cartContent
              }
            }));

            // Reset section after successful add to cart
            this.resetSection();
          }

          this.isAddingToCart = false;

        } catch (error) {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }
          this.isAddingToCart = false;

          const form = this.querySelector('form');
          if (form) {
            form.dispatchEvent(new CustomEvent('cart:error', {
              bubbles: true,
              detail: { error: error.message }
            }));
          }

          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      }

      resetSection() {
        // Reset all box selections
        this.boxes = {
          1: { startingProduct: this.boxes[1].startingProduct, selectedProduct: null },
          2: { startingProduct: this.boxes[2].startingProduct, selectedProduct: null },
          3: { startingProduct: this.boxes[3].startingProduct, selectedProduct: null }
        };

        this.activeBox = 1;
        this.currentBrowsing = null;

        // Reset gift card fields
        const giftCardNone = this.querySelector('input[name="properties[Gift Card Type]"][value=""]');
        if (giftCardNone) {
          giftCardNone.checked = true;
          this.handleGiftCardChange(giftCardNone);
        }

        const titleField = this.querySelector('input[name="properties[Gift Card Title]"]');
        const messageField = this.querySelector('textarea[name="properties[Gift Card Message]"]');
        if (titleField) titleField.value = '';
        if (messageField) messageField.value = '';

        // Reload the starting product for box 1
        this.loadBoxStartingProduct(1);

        // Update UI
        this.updateUI();
      }

      initializeDefaultSelection() {
        const flowerInput = this.querySelector('input[name="second_step_product"]:checked');
        const boxInput = this.querySelector('input[name="third_step_product"]:checked');

        if (flowerInput && boxInput) {
          const productIndex = parseInt(flowerInput.value);
          const selectedProduct = this.parsedProducts[productIndex];
          const boxIndex = parseInt(boxInput.value);
          const finalProduct = selectedProduct?.boxes[boxIndex];

          if (finalProduct) {
            this.fetchProductPage(`/products/${finalProduct.handle}`).then(async doc => {
              this.currentBrowsing = {
                product: finalProduct,
                document: doc,
                isFlowerOnly: false
              };
              // Update preview with initial product data
              await this.updateActiveBoxPreviewRealtime(doc, finalProduct);
            });
          }
        }
      }

      async updateForFlowerSelection(selectedProduct) {
        const productUrl = `/products/${selectedProduct.handle}`;
        const doc = await this.fetchProductPage(productUrl);

        this.updateGalleryFromDoc(doc);
        this.updateBoxColorOptions(selectedProduct);
        this.scrollToTop();

        this.currentBrowsing = {
          product: selectedProduct,
          document: doc,
          isFlowerOnly: true
        };

        await this.updateActiveBoxPreviewRealtime(doc, selectedProduct);
      }

      async updateForBoxSelection(finalProduct) {
        const productUrl = `/products/${finalProduct.handle}`;
        const doc = await this.fetchProductPage(productUrl);

        this.updateGalleryFromDoc(doc);
        this.scrollToTop();

        this.currentBrowsing = {
          product: finalProduct,
          document: doc,
          isFlowerOnly: false
        };

        await this.updateActiveBoxPreviewRealtime(doc, finalProduct);
      }

      async updateActiveBoxPreviewRealtime(doc, product) {
        const preview = this.querySelector(`[data-box-number="${this.activeBox}"]`);
        if (!preview) return;

        // Fetch product JSON for accurate prices
        let productData = null;
        try {
          productData = await this.fetchProductJson(product.handle);
        } catch (e) {
          // Silent fail - will use fallback values
        }

        // Get actual product price from product JSON
        const variant = productData?.variants?.[0] || productData;
        const actualPriceInCents = variant?.price || 0;

        // Use target price as the displayed price, actual price as compare-at (strikethrough)
        const displayPrice = this.formatMoney(this.targetPrice * 100);
        const comparePrice = actualPriceInCents ? this.formatMoney(actualPriceInCents) : '';

        // Get title from product JSON or fallback
        const title = productData?.title || product.title || '';

        // Get image from current gallery (after it's been updated)
        requestAnimationFrame(() => {
          const currentGallery = this.querySelector('product-gallery');
          const currentImg = currentGallery?.querySelector('.product__media.is-selected img') ||
            currentGallery?.querySelector('.product__media:first-child img') ||
            currentGallery?.querySelector('img');

          const image = currentImg?.currentSrc || currentImg?.src || '';

          // Update preview image
          const imgEl = preview.querySelector('[data-box-image]');
          if (imgEl && image) {
            imgEl.src = image;
          }

          // Update preview title
          const previewTitleEl = preview.querySelector('[data-box-title]');
          if (previewTitleEl && title) {
            previewTitleEl.textContent = title;
          }

          // Update preview price (target/bundle price)
          const previewPriceEl = preview.querySelector('[data-box-price] .first-step--price');
          if (previewPriceEl) {
            previewPriceEl.textContent = displayPrice;
          }

          // Update compare at price (actual product price as strikethrough)
          const priceContainer = preview.querySelector('[data-box-price]');
          let previewCompareEl = preview.querySelector('[data-box-price] .first-step--compare_at_price');

          if (comparePrice && comparePrice !== displayPrice) {
            if (!previewCompareEl && priceContainer) {
              previewCompareEl = document.createElement('span');
              previewCompareEl.className = 'first-step--compare_at_price';
              priceContainer.appendChild(previewCompareEl);
            }
            if (previewCompareEl) {
              previewCompareEl.textContent = comparePrice;
              previewCompareEl.style.display = '';
            }
          } else if (previewCompareEl) {
            previewCompareEl.style.display = 'none';
          }
        });
      }

      async confirmCurrentBox(options = {}) {
        const { skipUIUpdate = false } = options;

        if (!this.currentBrowsing) {
          const flowerInput = this.querySelector('input[name="second_step_product"]:checked');
          const boxInput = this.querySelector('input[name="third_step_product"]:checked');

          if (!flowerInput || !boxInput) return;

          const productIndex = parseInt(flowerInput.value);
          const selectedProduct = this.parsedProducts[productIndex];
          const boxIndex = parseInt(boxInput.value);
          const finalProduct = selectedProduct?.boxes[boxIndex];

          if (!finalProduct) return;

          const doc = await this.fetchProductPage(`/products/${finalProduct.handle}`);
          this.currentBrowsing = {
            product: finalProduct,
            document: doc,
            isFlowerOnly: false
          };
        }

        const { product, document: doc } = this.currentBrowsing;

        // Get variant ID
        let variantId = product.variantId;
        if (!variantId) {
          variantId = this.querySelector('input[name="id"]')?.value;
        }
        if (!variantId) {
          variantId = doc.querySelector('input[name="id"]')?.value;
        }
        if (!variantId && product.id) return;

        if (variantId && variantId.includes('gid://')) {
          const match = variantId.match(/\/(\d+)$/);
          if (match) variantId = match[1];
        }

        // Get current preview element (already updated by updateActiveBoxPreviewRealtime)
        const preview = this.querySelector(`[data-box-number="${this.activeBox}"]`);

        // Get title from current preview (already updated)
        const previewTitleEl = preview?.querySelector('[data-box-title]');
        const title = previewTitleEl?.textContent?.trim() || product.title || '';

        // Get display price (target/bundle price) from current preview
        const previewPriceEl = preview?.querySelector('[data-box-price] .first-step--price');
        const displayPrice = previewPriceEl?.textContent?.trim() || '';

        // Get compare-at price (actual product price) from current preview
        const previewCompareEl = preview?.querySelector('[data-box-price] .first-step--compare_at_price');
        const comparePrice = previewCompareEl?.textContent?.trim() || '';

        // Get image from current gallery
        const currentGallery = this.querySelector('product-gallery');
        const currentImg = currentGallery?.querySelector('.product__media.is-selected img') ||
          currentGallery?.querySelector('.product__media:first-child img') ||
          currentGallery?.querySelector('img[loading="eager"]') ||
          currentGallery?.querySelector('img');

        const image = currentImg?.currentSrc || currentImg?.src || '';

        const properties = this.collectProperties();

        // Save to active box
        this.boxes[this.activeBox].selectedProduct = {
          id: product.id,
          handle: product.handle,
          variantId: variantId,
          title: title,
          displayPrice: displayPrice,
          comparePrice: comparePrice,
          image: image,
          properties: properties
        };


        // Skip UI updates if we're about to add to cart and reset
        if (!skipUIUpdate) {
          this.updateBoxPreview(this.activeBox);

          // Move to next box or finish
          if (this.activeBox < 3) {
            this.activeBox++;

            // Clear gift card fields for the next box
            this.clearGiftCardFields();

            await this.loadBoxStartingProduct(this.activeBox);
            this.scrollToTop();
          }

          this.updateUI();
        }

        this.currentBrowsing = null;
      }

      clearGiftCardFields() {
        // Reset gift card type to "No card" option
        const giftCardNone = this.querySelector('input[name="properties[Gift Card Type]"][value=""]');
        if (giftCardNone) {
          giftCardNone.checked = true;
          this.handleGiftCardChange(giftCardNone);
        }

        // Clear title and message fields
        const titleField = this.querySelector('input[name="properties[Gift Card Title]"]');
        const messageField = this.querySelector('textarea[name="properties[Gift Card Message]"]');
        if (titleField) titleField.value = '';
        if (messageField) messageField.value = '';
      }

      updateBoxPreview(boxNum) {
        const preview = this.querySelector(`[data-box-number="${boxNum}"]`);
        if (!preview) return;

        const selected = this.boxes[boxNum].selectedProduct;
        if (!selected) return;

        const img = preview.querySelector('[data-box-image]');
        if (img && selected.image) {
          img.src = selected.image;
          img.alt = selected.title;
        }

        const title = preview.querySelector('[data-box-title]');
        if (title) {
          title.textContent = selected.title;
        }

        // Price is already set correctly (target price) - don't change it
        // Compare-at price (actual product price) should remain visible

        const subtitle = preview.querySelector('[data-box-subtitle]');
        if (subtitle) {
          subtitle.textContent = 'Selected';
        }

        const badge = preview.querySelector('[data-box-badge]');
        if (badge) {
          badge.setAttribute('data-status', 'selected');
          badge.textContent = 'SELECTED';
        }

        preview.setAttribute('data-status', 'selected');
      }

      async loadBoxStartingProduct(boxNum) {
        if (this.isProcessing) return;

        const starting = this.boxes[boxNum].startingProduct;
        if (!starting?.handle) return;

        this.isProcessing = true;

        try {
          const url = `/products/${starting.handle}?section_id=${this.sectionId}`;
          const response = await fetch(url);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');

          const newComponent = doc.querySelector(`triple-box-bundle[data-section-id="${this.sectionId}"]`);
          if (newComponent) {
            newComponent._transferredState = {
              boxes: this.boxes,
              activeBox: this.activeBox,
              parsedProducts: this.parsedProducts,
              targetPrice: this.targetPrice
            };

            this.replaceWith(newComponent);
          }
        } finally {
          setTimeout(() => {
            this.isProcessing = false;
          }, 500);
        }
      }

      async loadSwatchesForActiveBox() {
        const starting = this.boxes[this.activeBox].startingProduct;
        if (!starting?.tag) return;

        try {
          const products = await this.fetchSwatchData(starting.tag);
          this.parsedProducts = this.parseProducts(products);
          this.renderSwatches(starting.flowerColor, starting.boxColor);
        } catch (error) {
          // Silent fail - swatches won't load
        }
      }

      async fetchSwatchData(tag) {
        const query = `query {
          products(first: 250, query: "tag:{${tag}}") {
            edges {
              node {
                id
                handle
                title
                availableForSale
                featuredImage { url }
                priceRange { minVariantPrice { amount } }
                variants(first: 1) {
                  edges {
                    node {
                      id
                      availableForSale
                    }
                  }
                }
                flower_image_url: metafield(key: "stc_flower_image", namespace: "custom") { value }
                box_image_url: metafield(key: "stc_box_image", namespace: "custom") { value }
                flower_name: metafield(key: "stc_flower_name", namespace: "custom") { value }
                box_color: metafield(key: "stc_box_color", namespace: "custom") { value }
              }
            }
          }
        }`;

        const response = await fetch('/api/2025-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': '8996d359b34dab28ad60af2c4c4f4b73'
          },
          body: JSON.stringify({ query })
        });

        const data = await response.json();
        return data.data.products.edges;
      }

      parseProducts(products) {
        const uniqueFlowers = new Set();
        const parsed = [];

        // Filter to only available products
        const availableProducts = products.filter(p => {
          const node = p.node;
          const variantAvailable = node.variants?.edges[0]?.node?.availableForSale;
          return node.availableForSale && variantAvailable !== false;
        });

        availableProducts.forEach(product => {
          const node = product.node;
          if (!node.flower_name?.value || !node.flower_image_url?.value) return;

          if (this.uniqueImagesOnly && uniqueFlowers.has(node.flower_image_url.value)) return;
          uniqueFlowers.add(node.flower_image_url.value);

          // Only include available boxes
          const boxes = availableProducts
            .filter(p => p.node.flower_name?.value === node.flower_name.value)
            .map(p => ({
              id: p.node.id,
              handle: p.node.handle,
              title: p.node.title,
              box_color: p.node.box_color?.value,
              box_image_url: p.node.box_image_url?.value,
              featured_image: p.node.featuredImage?.url,
              price: p.node.priceRange?.minVariantPrice?.amount,
              variantId: p.node.variants?.edges[0]?.node?.id || null,
              availableForSale: p.node.availableForSale
            }));

          // Only add flower if it has at least one available box
          if (boxes.length === 0) return;

          parsed.push({
            id: node.id,
            handle: node.handle,
            title: node.title,
            flower_color: node.flower_name.value,
            flower_image_url: node.flower_image_url.value,
            featured_image: node.featuredImage?.url,
            variantId: node.variants?.edges[0]?.node?.id || null,
            boxes: boxes
          });
        });

        if (this.sortOption === 'alphabetical') {
          parsed.sort((a, b) => a.flower_color.localeCompare(b.flower_color));
        } else if (this.sortOption === 'length') {
          parsed.sort((a, b) => a.flower_color.length - b.flower_color.length);
        }

        return parsed;
      }

      renderSwatches(selectedFlowerColor, selectedBoxColor = null) {
        const flowerContainer = this.querySelector('.second-step--container');
        if (flowerContainer) {
          const html = this.parsedProducts.map((item, index) => `
            <label class="second-step--flower-color" for="second_step_product-${index}" tabindex="0">
              <input 
                type="radio" 
                id="second_step_product-${index}" 
                name="second_step_product" 
                value="${index}" 
                ${item.flower_color === selectedFlowerColor ? 'checked' : ''}
              >
              <img 
                src="${item.flower_image_url}" 
                width="50" 
                height="50" 
                alt="${item.flower_color}" 
              >
            </label>
          `).join('');

          flowerContainer.innerHTML = html;
        }

        const selectedProduct = this.parsedProducts.find(p => p.flower_color === selectedFlowerColor) || this.parsedProducts[0];
        if (selectedProduct) {
          this.updateBoxColorOptions(selectedProduct, selectedBoxColor);
        }
      }

      updateBoxColorOptions(selectedProduct, selectedBoxColor = null) {
        const boxContainer = this.querySelector('.third-step--container');
        if (!boxContainer) return;

        // Find the index of the selected box color, default to first available
        let defaultIndex = 0;
        if (selectedBoxColor) {
          const matchIndex = selectedProduct.boxes.findIndex(box => box.box_color === selectedBoxColor);
          if (matchIndex !== -1) {
            defaultIndex = matchIndex;
          }
        }

        const html = selectedProduct.boxes.map((box, index) => `
          <label class="third-step--box-color" for="third_step_product-${index}" tabindex="0">
            <input
              type="radio"
              id="third_step_product-${index}"
              name="third_step_product"
              value="${index}"
              data-box-color="${box.box_color}"
              ${index === defaultIndex ? 'checked' : ''}
            >
            <img
              src="${box.box_image_url}"
              width="50"
              height="50"
              alt="${box.box_color}"
            >
          </label>
        `).join('');

        boxContainer.innerHTML = html;
      }

      async fetchProductPage(url) {
        const fullUrl = `${url}?section_id=${this.sectionId}`;
        const response = await fetch(fullUrl);
        const html = await response.text();
        return new DOMParser().parseFromString(html, 'text/html');
      }

      async fetchProductJson(handle) {
        const response = await fetch(`/products/${handle}.js`);
        return response.json();
      }

      formatMoney(cents) {
        // Simple money formatter - adjust based on store settings
        const amount = (cents / 100).toFixed(2);
        return `$${amount}`;
      }

      updateGalleryFromDoc(doc) {
        const newGallery = doc.querySelector('product-gallery');
        const currentGallery = this.querySelector('product-gallery');
        if (newGallery && currentGallery) {
          const clonedGallery = newGallery.cloneNode(true);

          requestAnimationFrame(() => {
            currentGallery.replaceWith(clonedGallery);

            requestAnimationFrame(() => {
              const currentImg = this.querySelector('product-gallery img');
              if (currentImg) {
                const preview = this.querySelector(`[data-box-number="${this.activeBox}"]`);
                const imgEl = preview?.querySelector('[data-box-image]');
                if (imgEl) {
                  imgEl.src = currentImg.currentSrc || currentImg.src;
                }
              }
            });
          });
        }
      }

      collectProperties() {
        const properties = {};

        const inputs = this.querySelectorAll('input[name^="properties["], textarea[name^="properties["]');
        inputs.forEach(input => {
          if (input.type === 'radio') {
            if (!input.checked) return;
          } else {
            if (input.disabled || !input.value) return;
          }

          const match = input.name.match(/properties\[(.+)\]/);
          if (match) {
            properties[match[1]] = input.value;
          }
        });

        return properties;
      }

      updateUI() {
        for (let i = 1; i <= 3; i++) {
          const preview = this.querySelector(`[data-box-number="${i}"]`);
          if (!preview) continue;

          const badge = preview.querySelector('[data-box-badge]');

          if (i === this.activeBox) {
            preview.setAttribute('data-status', 'selecting');
            if (badge) {
              badge.setAttribute('data-status', 'selecting');
              badge.textContent = 'SELECTING';
            }
          } else if (this.boxes[i].selectedProduct) {
            preview.setAttribute('data-status', 'selected');
            if (badge) {
              badge.setAttribute('data-status', 'selected');
              badge.textContent = 'SELECTED';
            }
          } else {
            preview.setAttribute('data-status', 'not-selected');
            if (badge) {
              badge.setAttribute('data-status', 'not-selected');
              badge.textContent = 'NOT SELECTED';
            }
          }
        }

        this.updateHeading();
        this.updateButton();
      }

      updateHeading() {
        const heading = this.querySelector('#first-step-heading');
        if (!heading) return;

        const boxNumber = this.activeBox;
        let headingText = '';

        if (boxNumber === 1) {
          headingText = 'Select your first box';
        } else if (boxNumber === 2) {
          headingText = 'Select your second box';
        } else if (boxNumber === 3) {
          headingText = 'Select your third box';
        }

        heading.textContent = headingText;
      }

      updateButton() {
        const button = this.querySelector('button[type="submit"]');
        if (!button) return;

        const selectedCount = Object.values(this.boxes).filter(b => b.selectedProduct).length;

        // Check if we're editing a previously selected box
        const isEditing = this.boxes[this.activeBox].selectedProduct !== null;

        if (isEditing) {
          // When editing a selected box, show "UPDATE BOX X"
          button.textContent = `UPDATE BOX ${this.activeBox}`;
          button.disabled = false;
        } else if (selectedCount === 0) {
          button.textContent = 'SELECT BOX 1';
          button.disabled = false;
        } else if (selectedCount === 1) {
          button.textContent = 'SELECT BOX 2';
          button.disabled = false;
        } else if (selectedCount === 2) {
          button.textContent = 'ADD TO CART';
          button.disabled = false;
        } else {
          button.textContent = 'ADD TO CART';
          button.disabled = false;
        }
      }

      handleGiftCardChange(input) {
        const detailsEl = this.querySelector('.fourth-step--container');
        const titleField = this.querySelector('input[name="properties[Gift Card Title]"]');
        const messageField = this.querySelector('textarea[name="properties[Gift Card Message]"]');

        if (!detailsEl || !titleField || !messageField) return;

        if (input.value === '') {
          detailsEl.open = false;
          titleField.disabled = true;
          messageField.disabled = true;
          titleField.required = false;
          messageField.required = false;
        } else if (input.value === 'Million eCard') {
          detailsEl.open = true;
          titleField.disabled = false;
          messageField.disabled = false;
          titleField.required = true;
          messageField.required = true;
        } else if (input.value === 'Paper Card') {
          detailsEl.open = true;
          titleField.disabled = true;
          messageField.disabled = false;
          titleField.required = false;
          messageField.required = true;
        }
      }

      scrollToTop() {
        const section = document.getElementById(`shopify-section-${this.sectionId}`);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    customElements.define('triple-box-bundle', TripleBoxBundle);
    window.TripleBoxBundle = TripleBoxBundle;
  }
})();
